import { computeEvaluation } from "./scoringEval";
import { formatTimeLine } from "../utils/formatSeconds";
import { makeParquetQuerier } from "./parquetQuery";
import { matchesRange } from "./rangeMatch";

const queryParquet = makeParquetQuerier("Color Trails");

// Look up raw score in norms table → percentile string
function lookupNormsPC(normsData, scoreColumn, rawScore) {
  for (const row of normsData) {
    if (matchesRange(rawScore, row[scoreColumn])) {
      return row["%ile"];
    }
  }
  return null;
}

// Look up value in rawscore table → percentile range string
function lookupRawscorePC(rawscoreData, column, rawValue) {
  for (const row of rawscoreData) {
    if (matchesRange(rawValue, row[column])) {
      return row["%ile_range"];
    }
  }
  return null;
}

/**
 * Score a Color Trail test (CT1 or CT2).
 * @param {1|2} trailNumber - 1 for Color Trail 1, 2 for Color Trail 2
 * @param {string|number} temps - raw time in seconds
 * @param {string|number} erreurs - raw error count
 * @param {number} age - patient age
 * @param {number} education - years of education
 * @param {string|number|null} ct1Temps - CT1 time (needed for CT2 interference index)
 */
export async function calcColorTrailScore(
  trailNumber,
  temps,
  erreurs,
  age,
  education,
  ct1Temps = null,
  lang = "fr"
) {
  // 1. Find matching block for age + education
  const map = await queryParquet("colortrailmap.parquet");
  const block = map.find(
    (b) =>
      age >= b.age_min &&
      age <= b.age_max &&
      education >= b.edu_min &&
      education <= b.edu_max
  );

  if (!block) {
    return {
      error: lang === "de"
        ? `Kein Block für Alter=${age}, Bildung=${education} gefunden`
        : `Aucun bloc trouvé pour âge=${age}, scolarité=${education}`,
    };
  }

  // 2. Norms lookup: temps → percentile
  const normsData = await queryParquet(block.norms_file);

  const scoreCol = trailNumber === 1 ? "ct1_score" : "ct2_score";
  const tempsPC = lookupNormsPC(normsData, scoreCol, parseFloat(temps));

  // 3. Rawscore lookup: erreurs → percentile range
  let erreursPC = null;
  let indicePC = null;

  if (block.rawscore_file) {
    const rawscoreData = await queryParquet(block.rawscore_file);

    // CT1 uses ct1_errors, CT2 uses ct2_color_errors
    const errCol = trailNumber === 1 ? "ct1_errors" : "ct2_color_errors";
    erreursPC = lookupRawscorePC(rawscoreData, errCol, parseFloat(erreurs));

    // 4. Interference index for CT2 (CT2_time / CT1_time)
    if (trailNumber === 2 && ct1Temps && parseFloat(ct1Temps) > 0) {
      const ratio = parseFloat(temps) / parseFloat(ct1Temps);
      indicePC = lookupRawscorePC(
        rawscoreData,
        "interference_index",
        Math.round(ratio * 10) / 10
      );
    }
  }

  // 5. Build resultData
  const resultLoc = trailNumber === 1 ? "G15" : "G30";

  const isDe = lang === "de";
  const resultData = [
    { name: "tempsPC", label: isDe ? "Zeit PC" : "Temps PC", value: tempsPC ?? "—", rawTemps: temps },
    {
      name: "erreursPC",
      label: isDe ? "Fehler PC" : "Erreurs PC",
      value: erreursPC ?? "—",
      italic: !erreursPC,
    },
  ];

  if (trailNumber === 2) {
    const ct1Missing = !(ct1Temps && parseFloat(ct1Temps) > 0);
    const indiceValue = indicePC ?? (ct1Missing
      ? (isDe ? "Zeit CT1 angeben" : "Renseignez le temps du CT1")
      : "—");
    resultData.push({
      name: "indicePC",
      label: isDe ? "Index PC" : "Indice PC",
      value: indiceValue,
      italic: !indicePC,
      error: ct1Missing,
    });
  }

  const resultText = resultData
    .map((r) => (r.rawTemps != null && r.rawTemps !== "" ? formatTimeLine(r.label, r.rawTemps, r.value) : `${r.label}= ${r.value}`))
    .join("\n");
  const evaluation = computeEvaluation(resultData, resultLoc);

  return { resultLoc, resultData, resultText, evaluation, block: block.block };
}
